import { lazy, Suspense, type ComponentType } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import { Center, Spinner } from "@chakra-ui/react";
import Login from "./pages/Login.tsx";
import AuthCallback from "./pages/AuthCallback.tsx";
import Home from "./pages/Home.tsx";
import MainLayout from "./components/Layout/MainLayout";
import RequireAccess from "./components/auth/RequireAccess.tsx";

// Recover from a stale code-split chunk after a new deploy: a browser holding
// the old index.html requests chunk filenames that 404 once the new build is
// live, so the dynamic import() rejects and the route silently fails to render
// ("the URL changes but the page doesn't load"). On that failure we reload once
// (guarded against loops) to pull the fresh index + chunk names.
function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    const RELOAD_KEY = "nova.chunkReloaded";
    try {
      const mod = await factory();
      sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch (err) {
      if (!sessionStorage.getItem(RELOAD_KEY)) {
        sessionStorage.setItem(RELOAD_KEY, "1");
        window.location.reload();
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}

// Heavy, route-specific pages are code-split so the initial bundle stays small;
// each loads on demand behind the Suspense boundary in MainLayout (or the
// wrapper below for top-level routes).
const MyWorkspace = lazyWithRetry(() => import("./pages/MyWorkspace.tsx"));
const Participants = lazyWithRetry(() => import("./pages/Participants.tsx"));
const ParticipantSummary = lazyWithRetry(() => import("./pages/ParticipantSummary.tsx"));
const ParticipantProgram = lazyWithRetry(() => import("./pages/ParticipantProgram.tsx"));
const ParticipantDetails = lazyWithRetry(() => import("./pages/ParticipantDetails.tsx"));
const AdminCenter = lazyWithRetry(() => import("./pages/AdminCenter.tsx"));
const AdminUsers = lazyWithRetry(() => import("./pages/adminCenter/Users.tsx"));
const CareTeamMembers = lazyWithRetry(() => import("./pages/adminCenter/CareTeamMembers.tsx"));
const RolesPermissions = lazyWithRetry(() => import("./pages/adminCenter/RolesPermissions.tsx"));
const Organizations = lazyWithRetry(() => import("./pages/adminCenter/Organizations.tsx"));
const CourtsPrograms = lazyWithRetry(() => import("./pages/adminCenter/CourtsPrograms.tsx"));
const AccountSettings = lazyWithRetry(() => import("./pages/adminCenter/AccountSettings.tsx"));
const ListOfValues = lazyWithRetry(() => import("./pages/adminCenter/ListOfValues.tsx"));
const Calendar = lazyWithRetry(() => import("./pages/Calendar.tsx"));
const ReportAnalytics = lazyWithRetry(() => import("./pages/reportAnalytics/ReportAnalytics.tsx"));
const Referrals = lazyWithRetry(() => import("./pages/Referrals.tsx"));
const ReferralDetails = lazyWithRetry(() => import("./pages/ReferralDetails.tsx"));
const ProgressPreviewPage = lazyWithRetry(
  () => import("./pages/participantProgram/ProgressPreviewPage.tsx"),
);

const PageFallback = () => (
  <Center h="100%" w="100%" py="20">
    <Spinner size="lg" />
  </Center>
);

const withSuspense = (node: React.ReactNode) => (
  <Suspense fallback={<PageFallback />}>{node}</Suspense>
);

const router = createBrowserRouter([
  { path: "/login", element: <Login /> },
  { path: "/auth/callback", element: <AuthCallback /> },
  ...(import.meta.env.DEV
    ? [
        {
          path: "/__dev/progress-preview",
          element: withSuspense(<ProgressPreviewPage />),
        },
      ]
    : []),
  {
    path: "/",
    element: <MainLayout />,
    children: [
      { path: "workspace", element: <MyWorkspace /> },
      { path: "", element: <Navigate to="/workspace" replace /> },
      { path: "referrals", element: <Referrals /> },
      { path: "referrals/:id", element: <ReferralDetails /> },
      { path: "participants", element: <Participants /> },
      { path: "participants/:id", element: <ParticipantSummary /> },
      { path: "participants/:id/details", element: <ParticipantDetails /> },
      {
        path: "participants/:id/program/:programId/:ppcId",
        element: <ParticipantProgram />,
      },
      { path: "tasks", element: <Home /> },
      { path: "calendar", element: <Calendar /> },
      {
        path: "reports",
        element: (
          <RequireAccess rowId="reports">
            <ReportAnalytics />
          </RequireAccess>
        ),
      },
      {
        path: "admin",
        element: (
          <RequireAccess anyOf={["admin-settings", "care-team-members"]}>
            <AdminCenter />
          </RequireAccess>
        ),
      },
      // Admin Center sub-routes
      {
        path: "admin/care-team-members",
        element: (
          <RequireAccess anyOf={["admin-settings", "care-team-members"]}>
            <CareTeamMembers />
          </RequireAccess>
        ),
      },
      {
        path: "admin/users",
        element: (
          <RequireAccess rowId="admin-settings">
            <AdminUsers />
          </RequireAccess>
        ),
      },
      {
        path: "admin/organizations",
        element: (
          <RequireAccess rowId="admin-settings">
            <Organizations />
          </RequireAccess>
        ),
      },
      {
        path: "admin/roles-permissions",
        element: (
          <RequireAccess rowId="admin-settings">
            <RolesPermissions />
          </RequireAccess>
        ),
      },
      {
        path: "admin/courts-programs",
        element: (
          <RequireAccess rowId="admin-settings">
            <CourtsPrograms />
          </RequireAccess>
        ),
      },
      {
        path: "admin/account-settings",
        element: (
          <RequireAccess rowId="admin-settings">
            <AccountSettings />
          </RequireAccess>
        ),
      },
      {
        path: "admin/sla-manager",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/checklists",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/workflow-builder",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/channels-routing",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/triggers",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/delivery-logs",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/list-of-values",
        element: (
          <RequireAccess rowId="list-of-values">
            <ListOfValues />
          </RequireAccess>
        ),
      },
      {
        path: "admin/court-registry",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/docket-codes",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/sync-monitor",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/acronyms-terms",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/email-templates",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/progress-report-templates",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/document-types",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/integration-status",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/usage-limits",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/backups-dr",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
      {
        path: "admin/audit-change-log",
        element: (
          <RequireAccess rowId="admin-settings">
            <Home />
          </RequireAccess>
        ),
      },
    ],
  },
]);

export default function AppRoutes() {
  return <RouterProvider router={router} />;
}
